"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  resolveTopNavItems,
  SERVICE_NAV_CHILD_LINKS,
  TOP_NAV_ITEM_COUNT,
} from "@/lib/navigation/site-surfaces";
import { LogoNavMenu } from "@/components/nav/LogoNavMenu";
import CTA from "@/components/ui/CTA";
import { SettingsButton } from "@/components/Settings/SettingsButton";
import ServicesDropdown from "@/components/ServicesDropdown";

export default function Nav() {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<"services" | null>(null);

  return (
    <nav className="fixed left-0 right-0 top-0 z-[150] transition-colors duration-300">
      <div className="relative z-[160] container mx-auto px-4 py-2.5 md:py-3">
        <div className="overflow-visible rounded-xl border border-black/10 bg-tan/90 shadow-[0_14px_44px_rgba(0,0,0,0.1)] backdrop-blur-sm dark:border-white/12 dark:bg-background-dark/95 dark:shadow-[0_14px_44px_rgba(0,0,0,0.52)] dark:backdrop-blur-md">
          <div className="flex items-center justify-between gap-3 px-4 py-2 md:gap-4 md:px-5 md:py-2.5">
            <LogoNavMenu />

            {/* Desktop Nav */}
            <div className="hidden items-center gap-8 lg:flex">
              {resolveTopNavItems().map((item) => {
                if (item.kind === "services") {
                  return (
                    <ServicesDropdown
                      key={item.id}
                      isOpen={openDropdown === "services"}
                      onToggle={() =>
                        setOpenDropdown(
                          openDropdown === "services" ? null : "services",
                        )
                      }
                      label={item.label}
                      overviewHref={item.overviewHref}
                      serviceLinks={SERVICE_NAV_CHILD_LINKS}
                    />
                  );
                }
                return (
                  <a
                    key={item.id}
                    href={item.href}
                    className="group relative flex items-center px-1 py-2 text-text-light transition-all duration-300 hover:text-text-muted-light dark:text-text-dark dark:hover:text-text-muted-dark"
                  >
                    <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-current opacity-0 transition-all duration-300 group-hover:w-full group-hover:opacity-100" />
                    {item.label}
                  </a>
                );
              })}
              <CTA
                href="/contact"
                className="inline-flex shrink-0 items-center justify-center"
              >
                Contact Us
              </CTA>
            </div>
            <motion.button
              className="relative z-[170] mr-0 flex h-9 w-9 shrink-0 items-center justify-center text-text-light dark:text-text-dark lg:hidden"
              onClick={() => setIsOpen((prev) => !prev)}
              aria-label="Toggle menu"
              initial={false}
              animate={isOpen ? "open" : "closed"}
            >
              <div className="relative h-5 w-5">
                <motion.span
                  className="absolute left-0 top-[2px] h-0.5 w-full origin-center bg-current"
                  variants={{
                    open: { rotate: 45, y: 7.5 },
                    closed: { rotate: 0, y: 0 },
                  }}
                  transition={{ duration: 0.2 }}
                />
                <motion.span
                  className="absolute left-0 top-[9px] h-0.5 w-full bg-current"
                  variants={{
                    open: { opacity: 0 },
                    closed: { opacity: 1 },
                  }}
                  transition={{ duration: 0.2 }}
                />
                <motion.span
                  className="absolute bottom-[2px] left-0 h-0.5 w-full origin-center bg-current"
                  variants={{
                    open: { rotate: -45, y: -7.5 },
                    closed: { rotate: 0, y: 0 },
                  }}
                  transition={{ duration: 0.2 }}
                />
              </div>
            </motion.button>
          </div>
        </div>
        <SettingsButton />
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[120] bg-background-light dark:bg-background-dark"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container mx-auto px-4 py-20">
              <div className="space-y-6">
                {resolveTopNavItems().map((item, i) => {
                  if (item.kind === "services") {
                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                      >
                        <div className="mb-4 text-3xl text-text-light dark:text-text-dark">
                          {item.label}
                        </div>
                        <div className="space-y-3 pl-6">
                          {SERVICE_NAV_CHILD_LINKS.map((child) => (
                            <Link
                              key={child.href}
                              href={child.href}
                              className="block text-xl text-text-muted-light transition-colors hover:text-brand-teal dark:text-text-muted-dark"
                              onClick={() => setIsOpen(false)}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    );
                  }
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <Link
                        href={item.href}
                        className="block text-3xl text-text-light transition-colors hover:text-brand-teal dark:text-text-dark"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  );
                })}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: TOP_NAV_ITEM_COUNT * 0.1 }}
                >
                  <CTA href="/contact" className="text-2xl">
                    Contact Us
                  </CTA>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
